import React, { useRef } from 'react'
import {
  Box,
  Typography,
  Button,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { GridColDef, GridRowModel } from '@mui/x-data-grid'

export interface PrintableDataGridProps {
  title?: string
  subtitle?: string
  columns: GridColDef[]
  rows: GridRowModel[]
  showPrintButton?: boolean
  headerInfo?: {
    company?: string
    department?: string
    reportDate?: string
    generatedBy?: string
  }
  footerInfo?: {
    totalRecords?: number
    customText?: string
    timestamp?: string
  }
  summaryData?: Array<{
    label: string
    value: string | number
    formatted?: string
  }>
  pageSize?: number
}

export const PrintableDataGrid: React.FC<PrintableDataGridProps> = ({
  title,
  subtitle,
  columns,
  rows,
  showPrintButton = true,
  headerInfo = {},
  footerInfo = {},
  summaryData = [],
  pageSize = 25,
}) => {
  const printRef = useRef<HTMLDivElement>(null)

  const handlePrint = () => {
    if (!printRef.current) return

    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    const printContent = printRef.current.innerHTML

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title || 'Reporte de Gerencia de Proyectos'}</title>
          <style>
            @media print {
              @page {
                size: A4 landscape;
                margin: 1cm;
              }
              
              body {
                font-family: 'Roboto', Arial, sans-serif;
                font-size: 10px;
                line-height: 1.2;
                margin: 0;
                padding: 0;
                color: #000;
              }
              
              .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #203a5d;
                padding-bottom: 10px;
              }
              
              .print-header h1 {
                font-size: 16px;
                margin: 0 0 5px 0;
                color: #203a5d;
                font-weight: bold;
              }
              
              .print-header h2 {
                font-size: 12px;
                margin: 0 0 5px 0;
                color: #666;
                font-weight: normal;
              }
              
              .print-header .meta-info {
                font-size: 9px;
                color: #888;
                margin-top: 5px;
              }
              
              .print-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 15px;
                font-size: 9px;
              }
              
              .print-table th {
                background-color: #f5f5f5;
                border: 1px solid #ddd;
                padding: 4px 6px;
                font-weight: bold;
                text-align: left;
                color: #203a5d;
              }
              
              .print-table td {
                border: 1px solid #ddd;
                padding: 3px 6px;
                word-wrap: break-word;
                max-width: 200px;
              }
              
              .print-table tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              
              .print-summary {
                margin-top: 15px;
                padding: 10px;
                background-color: #f0f7ff;
                border: 1px solid #203a5d;
              }
              
              .print-summary h3 {
                font-size: 12px;
                margin: 0 0 10px 0;
                color: #203a5d;
              }
              
              .print-summary-item {
                display: flex;
                justify-content: space-between;
                margin-bottom: 3px;
                font-size: 10px;
              }
              
              .print-footer {
                margin-top: 20px;
                padding-top: 10px;
                border-top: 1px solid #ddd;
                font-size: 8px;
                color: #666;
                text-align: center;
              }
              
              .currency {
                text-align: right;
                font-family: monospace;
              }
              
              .date {
                text-align: center;
              }
              
              .status {
                text-align: center;
                font-weight: bold;
              }
              
              .page-break {
                page-break-before: always;
              }
              
              .no-print {
                display: none;
              }
            }
            
            @media screen {
              body {
                font-family: 'Roboto', Arial, sans-serif;
                padding: 20px;
                background-color: #f5f5f5;
              }
              
              .print-container {
                background: white;
                padding: 30px;
                border-radius: 8px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                max-width: 100%;
                margin: 0 auto;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printContent}
          </div>
        </body>
      </html>
    `)

    printWindow.document.close()
    printWindow.focus()
    
    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 500)
  }

  const formatCellValue = (value: any, column: GridColDef): string => {
    if (value == null || value === '') return '-'

    // Format based on column type or field name
    if (column.type === 'number' || 
        column.field.toLowerCase().includes('valor') || 
        column.field.toLowerCase().includes('monto') || 
        column.field.toLowerCase().includes('saldo')) {
      const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value
      if (!isNaN(numValue)) {
        return new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency: 'COP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(numValue)
      }
    }

    if (column.type === 'date' || column.field.toLowerCase().includes('fecha')) {
      try {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return new Intl.DateTimeFormat('es-CO', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(date)
        }
      } catch {
        // Fall through to default
      }
    }

    return String(value)
  }

  const getCellClassName = (column: GridColDef): string => {
    if (column.type === 'number' || 
        column.field.toLowerCase().includes('valor') || 
        column.field.toLowerCase().includes('monto') || 
        column.field.toLowerCase().includes('saldo')) {
      return 'currency'
    }
    
    if (column.type === 'date' || column.field.toLowerCase().includes('fecha')) {
      return 'date'
    }
    
    if (column.field.toLowerCase().includes('estado') || 
        column.field.toLowerCase().includes('status')) {
      return 'status'
    }
    
    return ''
  }

  // Split rows into pages for better printing
  const pageCount = Math.ceil(rows.length / pageSize)
  const pages = Array.from({ length: pageCount }, (_, index) => {
    const startIndex = index * pageSize
    const endIndex = Math.min(startIndex + pageSize, rows.length)
    return rows.slice(startIndex, endIndex)
  })

  return (
    <Box>
      {showPrintButton && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ minWidth: 120 }}
          >
            Imprimir
          </Button>
        </Box>
      )}

      <div ref={printRef}>
        {/* Header */}
        <div className="print-header">
          {headerInfo.company && (
            <Typography variant="h4" component="h1" sx={{ fontSize: '16px', fontWeight: 'bold', color: '#203a5d', mb: 0.5 }}>
              {headerInfo.company}
            </Typography>
          )}
          
          {headerInfo.department && (
            <Typography variant="h5" component="h2" sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>
              {headerInfo.department}
            </Typography>
          )}
          
          {title && (
            <Typography variant="h6" component="h3" sx={{ fontSize: '14px', fontWeight: 'bold', color: '#203a5d', mb: 0.5 }}>
              {title}
            </Typography>
          )}
          
          {subtitle && (
            <Typography variant="body2" sx={{ fontSize: '11px', color: '#666', mb: 1 }}>
              {subtitle}
            </Typography>
          )}
          
          <div className="meta-info">
            {headerInfo.reportDate && (
              <span>Fecha del reporte: {headerInfo.reportDate}</span>
            )}
            {headerInfo.generatedBy && (
              <span style={{ marginLeft: '20px' }}>Generado por: {headerInfo.generatedBy}</span>
            )}
          </div>
        </div>

        {/* Data Tables (one per page) */}
        {pages.map((pageRows, pageIndex) => (
          <div key={pageIndex} className={pageIndex > 0 ? 'page-break' : ''}>
            {pageIndex > 0 && (
              <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: '#666' }}>
                Página {pageIndex + 1} de {pageCount}
              </Typography>
            )}
            
            <table className="print-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.field}>
                      {column.headerName || column.field}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((column) => (
                      <td key={column.field} className={getCellClassName(column)}>
                        {formatCellValue(row[column.field], column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {/* Summary */}
        {summaryData.length > 0 && (
          <div className="print-summary">
            <Typography variant="h6" component="h3" sx={{ fontSize: '12px', fontWeight: 'bold', color: '#203a5d', mb: 1 }}>
              Resumen
            </Typography>
            {summaryData.map((item, index) => (
              <div key={index} className="print-summary-item">
                <span>{item.label}:</span>
                <span>{item.formatted || item.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="print-footer">
          {footerInfo.totalRecords && (
            <div>Total de registros: {footerInfo.totalRecords}</div>
          )}
          {footerInfo.timestamp && (
            <div>Fecha de generación: {footerInfo.timestamp}</div>
          )}
          {footerInfo.customText && (
            <div>{footerInfo.customText}</div>
          )}
          <div style={{ marginTop: '10px', fontSize: '8px' }}>
            Sistema AutoCE - Automatización y Control S.A.S.
          </div>
        </div>
      </div>
    </Box>
  )
}

export default PrintableDataGrid